const AUTH_STORAGE_KEY = 'vdc_auth_session';
const AUTH_NOTICE_STORAGE_KEY = 'vdc_auth_notice';
const REMEMBERED_LOGIN_STORAGE_KEY = 'vdc_remembered_login';
export const AUTH_SESSION_UPDATED_EVENT = 'vdc-auth-session-updated';

const getStorage = (rememberMe) => {
  if (typeof window === 'undefined') {
    return null;
  }

  return rememberMe ? window.localStorage : window.sessionStorage;
};

const getAvailableSession = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storages = [window.localStorage, window.sessionStorage];

  for (let index = 0; index < storages.length; index += 1) {
    const storage = storages[index];
    const rawValue = storage.getItem(AUTH_STORAGE_KEY);

    if (rawValue) {
      try {
        return JSON.parse(rawValue);
      } catch (error) {
        storage.removeItem(AUTH_STORAGE_KEY);
      }
    }
  }

  return null;
};

const getStorageWithSession = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storages = [window.localStorage, window.sessionStorage];

  for (let index = 0; index < storages.length; index += 1) {
    const storage = storages[index];

    if (storage.getItem(AUTH_STORAGE_KEY)) {
      return storage;
    }
  }

  return window.localStorage;
};

const dispatchAuthSessionUpdated = (sessionData) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(AUTH_SESSION_UPDATED_EVENT, {
    detail: sessionData || null,
  }));
};

const buildApiUrl = (path) => {
  const normalizedBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `${normalizedBaseUrl}${normalizedPath}`;
};

const parseResponseBody = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    return { message: text };
  }
};

const resolveErrorMessage = (status, payload) => {
  if (payload?.message) {
    return payload.message;
  }

  if (payload?.mensagem) {
    return payload.mensagem;
  }

  switch (status) {
    case 401:
      return 'Usuário ou senha inválidos.';
    case 403:
      return 'Usuário bloqueado por status ou análise.';
    default:
      return 'Não foi possível autenticar no momento.';
  }
};

const persistSession = (sessionData, rememberMe) => {
  const storage = getStorage(rememberMe);

  if (!storage) {
    return;
  }

  const payload = sessionData && typeof sessionData === 'object'
    ? sessionData
    : { authenticated: true };

  storage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
  dispatchAuthSessionUpdated(payload);
};

const persistRememberedLogin = (cpfCnpj, rememberMe) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!rememberMe || !cpfCnpj) {
    window.localStorage.removeItem(REMEMBERED_LOGIN_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(REMEMBERED_LOGIN_STORAGE_KEY, JSON.stringify({
    cpf_cnpj: cpfCnpj,
    remember_me: true,
  }));
};

export const clearAuthSession = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
  dispatchAuthSessionUpdated(null);
};

const persistAuthNotice = (message) => {
  if (typeof window === 'undefined' || !message) {
    return;
  }

  window.sessionStorage.setItem(AUTH_NOTICE_STORAGE_KEY, message);
};

export const consumeAuthNotice = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const message = window.sessionStorage.getItem(AUTH_NOTICE_STORAGE_KEY) || '';

  if (message) {
    window.sessionStorage.removeItem(AUTH_NOTICE_STORAGE_KEY);
  }

  return message;
};

export const getRememberedLogin = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawValue = window.localStorage.getItem(REMEMBERED_LOGIN_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    window.localStorage.removeItem(REMEMBERED_LOGIN_STORAGE_KEY);
    return null;
  }
};

export const redirectToLogin = (reason = '') => {
  if (typeof window === 'undefined') {
    return;
  }

  const loginPath = reason ? `/log_in?reason=${encodeURIComponent(reason)}` : '/log_in';

  if (window.location.pathname !== '/log_in') {
    window.location.assign(loginPath);
  }
};

export const handleUnauthorizedSession = () => {
  clearAuthSession();
  persistAuthNotice('Sua sessão expirou. Faça login novamente.');
  redirectToLogin('session-expired');
};

export const getAuthSession = () => getAvailableSession();

export const isAuthenticated = () => Boolean(getAvailableSession());

export const getDefaultAuthenticatedRoute = (session = getAvailableSession()) => {
  const userTypeId = Number(session?.tipoUsuario?.id || session?.usuario?.idTipoUsuario || 0);

  if (userTypeId === 3) {
    return '/pages/metricas-freelancer';
  }

  return '/pages/inicio';
};

export const updateAuthSession = (updater) => {
  const currentSession = getAvailableSession();

  if (!currentSession) {
    return null;
  }

  const nextSession = typeof updater === 'function'
    ? updater(currentSession)
    : {
      ...currentSession,
      ...(updater || {}),
    };

  const storage = getStorageWithSession();

  if (!storage) {
    return nextSession;
  }

  storage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession));
  dispatchAuthSessionUpdated(nextSession);

  return nextSession;
};

export const authenticateUser = async ({ cpf_cnpj: cpfCnpj, senha, rememberMe = false }) => {
  const payload = {
    cpf_cnpj: (cpfCnpj || '').trim(),
    senha: senha || '',
  };

  if (!payload.cpf_cnpj || !payload.senha) {
    throw new Error('Informe CPF/CNPJ e senha.');
  }

  const response = await fetch(buildApiUrl('/usuarios/login'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    throw new Error(resolveErrorMessage(response.status, responseBody));
  }

  persistRememberedLogin(payload.cpf_cnpj, rememberMe);
  persistSession(responseBody || { authenticated: true, cpf_cnpj: payload.cpf_cnpj }, rememberMe);

  return responseBody;
};
